import { useMemo, useState, useCallback, useEffect } from 'react';
import FilterDropdown from 'components/elements/Dropdown/FilterDropdown';
import { useAvailableCities } from 'hooks/useUserCityAccess';
import { useUserStateSelection, useUserMultiRegionSelection } from 'state/dashboard';
import { METROPOLITAN_CODE_TO_LABEL, Region } from 'utils/constants/location';

const RegionSelection = () => {
  const [selectedRegions, setSelectedRegions] = useUserMultiRegionSelection();
  const [selectedState] = useUserStateSelection();
  const { availableRegions, availableStates } = useAvailableCities();
  const [errorMessage, setErrorMessage] = useState<string | undefined>();

  const regionOptions = useMemo(() => {
    const regionsForState = availableStates[selectedState || 'TX'];

    if (!regionsForState) {
      return [];
    }

    return Object.keys(regionsForState.regions)
      .filter((key: string) => key in METROPOLITAN_CODE_TO_LABEL)
      .map((key: string) => {
        return {
          id: key,
          value: key,
          label: METROPOLITAN_CODE_TO_LABEL[key],
        };
      });
  }, [availableRegions, availableStates, selectedState]);

  useEffect(() => {
    if (!errorMessage) {
      return undefined;
    }

    const timer = setTimeout(() => {
      setErrorMessage(undefined);
    }, 4000);

    return () => {
      clearTimeout(timer);
    };
  }, [errorMessage]);

  const onSelectRegions = useCallback(
    (values: string[]) => {
      if (values.length === 0 && selectedRegions.length > 0) {
        setErrorMessage('You must have at least one region selected');

        return;
      }
      setErrorMessage(undefined);
      setSelectedRegions(values as Region[]);
    },
    [selectedRegions, setSelectedRegions],
  );

  const handleSetCheckedValues = useCallback(
    (values: Region[]) => {
      onSelectRegions(values);
    },
    [selectedRegions, setSelectedRegions, onSelectRegions],
  );

  return (
    <FilterDropdown
      options={regionOptions}
      onSubmit={onSelectRegions}
      renderClearAll={false}
      checkedValues={selectedRegions}
      setCheckedValues={handleSetCheckedValues}
      errorMessage={errorMessage}
      maxHeight="280px"
    >
      Region
    </FilterDropdown>
  );
};

export default RegionSelection;
